<template>
  <div>
    <v-overlay
      v-if="isAsync(column)"
      :model-value="isAsyncLoading"
      class="align-center justify-center"
    >
      <v-progress-circular
        color="primary"
        indeterminate
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-btn
      v-if="isType(cell, CellType.BUTTON)"
      class="button"
      @click="column.callback(row)"
    >
      <i :class="`mdi mdi-${cell.icon} mdi-24px`" />
      {{ transformField(cell) }}
    </v-btn>
    <router-link
      v-if="isType(cell, CellType.LINK)"
      :to="cell.linkTo(row)"
      class="button"
      @click.prevent.stop="scrollTop"
    >
      <i :class="`mdi mdi-${cell.icon} mdi-24px`" />
      {{ transformField(cell) }}
    </router-link>
    <code
      v-else-if="isType(cell, CellType.CODE) && value"
      class="has-text-grey"
      >{{ transformField(cell) }}</code
    >
    <multiline-tooltip
      v-else-if="
        hasTooltip(cell) &&
        isType(cell, CellType.TAG) &&
        value &&
        !isAsyncLoading
      "
      :label="cell.tooltip(row, index).label"
      position="is-bottom"
      type="is-black"
    >
      <span :class="'tag ' + getClass(row, cell)">{{
        transformField(cell)
      }}</span>
    </multiline-tooltip>
    <span
      v-else-if="isType(cell, CellType.TAG) && value && !isAsyncLoading"
      :class="'tag ' + getClass(row, cell)"
      >{{ transformField(cell) }}</span
    >
    <span v-else-if="isType(cell, CellType.ICON)" :class="getClass(row, cell)">
      <i :class="transformField(cell) + ' mdi-24px'" />
    </span>
    <editable-cell
      v-else-if="isType(cell, CellType.EDITABLE_CELL)"
      :value="transformField(cell)"
      @save="(newValue) => emit('cell-edited', row, column, newValue)"
    />
    <span v-else-if="isType(cell, CellType.IMAGE)">
      <img
        :src="transformField(cell)"
        alt="Placeholder image"
        class="ma-1"
        width="45"
      />
    </span>
    <div v-else-if="isType(cell, CellType.TEXT)" v-show="!isAsyncLoading">
      {{ transformField(cell) }}
      <multiline-tooltip
        v-if="hasTooltip(cell)"
        :label="cell.tooltip(row, index).label"
        position="is-bottom"
        type="is-black"
      >
        <span class="icon">
          <i
            :class="`mdi ${
              cell.tooltip(row, index).icon
                ? cell.tooltip(row, index).icon
                : 'mdi-help-circle-outline'
            } mdi-24px`"
          />
        </span>
      </multiline-tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import MultilineTooltip from '@/components/common/MultilineTooltip.vue'
import { CellType } from '@/models/core/base'
import { DataStateDisplay } from '@/util/DataStateDisplay'

import EditableCell from './EditableCell.vue'

const emit = defineEmits(['cell-edited'])
const props = defineProps({
  row: {
    required: true,
  },
  column: {
    required: true,
  },
  cell: {
    required: true,
    type: Object,
  },
  value: {
    required: true,
  },
  index: Number,
})

function isAsync(column) {
  return column.async === true
}

const isAsyncLoading = computed(() => {
  return props.value === DataStateDisplay.Loading
})

function isType(cell, type: CellType) {
  if (cell.type !== undefined) {
    return cell.type === type
  }
}

function hasTooltip(cell) {
  return cell.tooltip !== undefined
}

function transformField(cell) {
  if (cell.transform !== undefined) {
    return cell.transform(props.value)
  } else {
    return props.value
  }
}

function getClass(row, cell): string {
  if (cell.class !== undefined) {
    return cell.class(row)
  }
  return ''
}

function scrollTop() {
  window.scrollTo(0, 0)
}
</script>

<style lang="scss" scoped>
.base-table__cell .cell-image {
  height: inherit;
}

.button i.mdi {
  padding-right: 5px;
}
</style>
